import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { ProducerSettings } from "@/hooks/useProducerSettings";

interface Props {
  settings: ProducerSettings;
  onUpdate: (updates: Partial<ProducerSettings>) => void;
}

export function ProducerLocationSettings({ settings, onUpdate }: Props) {
  return (
    <Card className="space-y-6 p-6">
      <div>
        <h2 className="mb-2 text-xl font-semibold">Location tracking settings</h2>
        <p className="text-sm text-muted-foreground">
          Configure how GPS tracking works for your crew members.
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="font-medium">Tracking options</h3>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <Label htmlFor="tracking_enabled" className="block cursor-default">
                Enable location tracking
              </Label>
              <p className="text-sm text-muted-foreground">
                Allow crew members to share their location
              </p>
            </div>
            <Switch
              id="tracking_enabled"
              checked={settings.location_tracking_enabled}
              onCheckedChange={(checked) => onUpdate({ location_tracking_enabled: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex-1">
              <Label htmlFor="assignments_only" className="block cursor-default">
                Track only during assignments
              </Label>
              <p className="text-sm text-muted-foreground">
                Location updates only when crew has active tasks
              </p>
            </div>
            <Switch
              id="assignments_only"
              checked={settings.location_tracking_during_assignments_only}
              onCheckedChange={(checked) =>
                onUpdate({ location_tracking_during_assignments_only: checked })
              }
            />
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <Label>Tracking frequency</Label>
        <RadioGroup
          value={settings.tracking_frequency}
          onValueChange={(value: "low" | "standard" | "high") =>
            onUpdate({ tracking_frequency: value })
          }
          className="space-y-2"
        >
          <div className="flex items-center space-x-3">
            <RadioGroupItem value="low" id="freq_low" />
            <Label htmlFor="freq_low" className="cursor-pointer font-normal">
              Low (every 2 minutes)
            </Label>
          </div>
          <div className="flex items-center space-x-3">
            <RadioGroupItem value="standard" id="freq_standard" />
            <Label htmlFor="freq_standard" className="cursor-pointer font-normal">
              Standard (every 60 seconds)
            </Label>
          </div>
          <div className="flex items-center space-x-3">
            <RadioGroupItem value="high" id="freq_high" />
            <Label htmlFor="freq_high" className="cursor-pointer font-normal">
              High (every 10 seconds)
            </Label>
          </div>
        </RadioGroup>
      </div>

      <div className="space-y-3">
        <Label>Tracking accuracy</Label>
        <RadioGroup
          value={settings.tracking_accuracy}
          onValueChange={(value: "low" | "medium" | "high") =>
            onUpdate({ tracking_accuracy: value })
          }
          className="space-y-2"
        >
          <div className="flex items-center space-x-3">
            <RadioGroupItem value="low" id="acc_low" />
            <Label htmlFor="acc_low" className="cursor-pointer font-normal">
              Low accuracy
            </Label>
          </div>
          <div className="flex items-center space-x-3">
            <RadioGroupItem value="medium" id="acc_medium" />
            <Label htmlFor="acc_medium" className="cursor-pointer font-normal">
              Medium accuracy
            </Label>
          </div>
          <div className="flex items-center space-x-3">
            <RadioGroupItem value="high" id="acc_high" />
            <div>
              <Label htmlFor="acc_high" className="cursor-pointer font-normal">
                High accuracy
              </Label>
              <p className="text-xs text-muted-foreground">May impact battery life</p>
            </div>
          </div>
        </RadioGroup>

        {settings.tracking_accuracy === "high" && (
          <Alert className="border-amber-500/50 bg-amber-500/10">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <AlertDescription className="text-amber-500">
              High accuracy mode may significantly impact crew members' battery life.
            </AlertDescription>
          </Alert>
        )}
      </div>

      <div className="space-y-4">
        <h3 className="font-medium">Map display options</h3>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <Label htmlFor="highlight_far" className="block cursor-default">
                Highlight crew too far from stage
              </Label>
              <p className="text-sm text-muted-foreground">
                Show warning indicator on crew markers when they're not at their assigned stage
              </p>
            </div>
            <Switch
              id="highlight_far"
              checked={settings.highlight_crew_far_from_stage}
              onCheckedChange={(checked) => onUpdate({ highlight_crew_far_from_stage: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex-1">
              <Label htmlFor="show_gps_corrections" className="block cursor-default">
                Show GPS corrections
              </Label>
              <p className="text-sm text-muted-foreground">
                Display crew markers at corrected positions (recommended). Turn off to see raw GPS
                readings exactly as the device reports them.
              </p>
            </div>
            <Switch
              id="show_gps_corrections"
              checked={settings.show_gps_corrections !== false}
              onCheckedChange={(checked) => onUpdate({ show_gps_corrections: checked })}
            />
          </div>
        </div>
      </div>
    </Card>
  );
}
